import { Link } from 'react-router';
import { ArrowDown, ArrowUp, ChevronDown, Ellipsis, EyeOff, Folder, GitBranch, GitMerge, HeartPulse, Play, SlidersHorizontal } from '@orgus/ui/icons';
import { useState } from 'react';
import type { ReactNode } from 'react';
import type { Machine, Project } from '@orgus/protocol';
import {
  Badge,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  IconButton,
  Pressable,
  ProjectMark,
  REVEAL_GROUP,
  RelativeTime,
  RevealRest,
  RevealSlot,
  RunStrip,
  StateGroup,
  StateSummaryBar,
  Tooltip,
  WORK_STATES,
  baseName,
  cn,
  formatDuration,
  formatInt,
  plural,
  shortPath,
  shortSha,
} from '@orgus/ui';
import type { WorkState } from '@orgus/ui';
import { MachineRef, SessionRef } from '../entities/EntityRef.tsx';
import { runSummary } from '../overview/pulse.ts';
import { menuActions, reportingSummary } from '../project-health/model.ts';
import type { ProjectReport } from '../project-health/model.ts';
import { openDeclaredAction } from '../project-health/store.ts';
import { openProjectAutonomy } from '../autonomy/api.ts';
import { CARD_SURFACE } from '../machines/MachineCard.tsx';
import type { CheckoutRow } from './model.ts';
import { shortRemote } from './model.ts';
import { itemsIn } from './work.ts';
import type { ProjectWork, WorkItem } from './work.ts';

/** Group labels where the work-state word is not the one a project uses. */
export const PROJECT_STATE_LABEL: Partial<Record<WorkState, string>> = { stale: 'Idle' };

/** Adapter ids are internal; name the ones we know and keep unknown ones as they are. */
const ADAPTER_LABEL: Record<string, string> = { 'qawl-land': 'Landing queue', 'orgus-protocol': 'Pipeline' };

/** The project's one chip: a state file that stopped reporting (or an invalid orgus.yml) outranks the pipeline's name. */
export function projectChip(project: Project | undefined, reports: ProjectReport[]): { text: string; tone?: 'waiting'; variant?: 'outline' } | null {
  const protocolPipeline = reports.find((r) => r.pipelineSource === 'protocol');
  const adapter = protocolPipeline ? (protocolPipeline.manifest?.pipeline?.title ?? 'Pipeline') : (project?.adapters ?? []).find((a) => a !== 'generic-git');
  const off = reportingSummary(reports);
  return off ? { text: off === 'invalid' ? 'Invalid orgus.yml' : 'Not reporting', tone: 'waiting' } : adapter ? { text: ADAPTER_LABEL[adapter] ?? adapter, variant: 'outline' } : null;
}

/** The project's menu: its autonomy, health, declared actions, and stop watching per machine. */
export function ProjectMenu({
  project,
  machines,
  reports,
  onHealth,
  onStopWatching,
}: {
  project: Project;
  machines: Machine[];
  reports: ProjectReport[];
  onHealth: (projectId: string) => void;
  onStopWatching: (project: Project, machine: Machine) => void;
}) {
  const actionReports = reports.filter((r) => menuActions(r).length > 0);
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <IconButton label={`More for ${project.name}`} icon={<Ellipsis />} size="xs" />
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuItem icon={<SlidersHorizontal />} onSelect={() => openProjectAutonomy(project.id)}>
          Edit autonomy…
        </DropdownMenuItem>
        <DropdownMenuItem icon={<HeartPulse />} onSelect={() => onHealth(project.id)}>
          Open project health
        </DropdownMenuItem>
        {actionReports.length > 0 && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuLabel>Declared actions</DropdownMenuLabel>
            {actionReports.flatMap((r) =>
              menuActions(r).map((a) => (
                <DropdownMenuItem key={`${r.machineId}:${a.id}`} icon={<Play />} onSelect={() => openDeclaredAction({ projectId: r.projectId, machineId: r.machineId, actionId: a.id })}>
                  {actionReports.length > 1 ? `${a.label} on ${machines.find((m) => m.id === r.machineId)?.name ?? 'its machine'}…` : `${a.label}…`}
                </DropdownMenuItem>
              )),
            )}
          </>
        )}
        <DropdownMenuSeparator />
        <DropdownMenuLabel>Stop watching</DropdownMenuLabel>
        {machines.map((m) => (
          <DropdownMenuItem key={m.id} icon={<EyeOff />} danger onSelect={() => onStopWatching(project, m)}>
            On {m.name}…
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

/** Ahead and behind the default branch, blank when even. */
export function Drift({ row }: { row: CheckoutRow }) {
  const c = row.checkout;
  if (!c.ahead && !c.behind) return null;
  const base = row.project?.defaultBranch ?? 'main';
  return (
    <Tooltip content={`${formatInt(c.ahead)} commits not on ${base}, ${formatInt(c.behind)} commits on ${base} not here`} delay={300}>
      <span tabIndex={-1} className="inline-flex items-center gap-2 text-caption text-fg-subtle num">
        <span className={cn('inline-flex items-center gap-1', c.ahead && 'text-fg-muted')}>
          <ArrowUp className="size-3" aria-hidden />
          {formatInt(c.ahead)}
        </span>
        <span className={cn('inline-flex items-center gap-1', c.behind && 'text-fg-muted')}>
          <ArrowDown className="size-3" aria-hidden />
          {formatInt(c.behind)}
        </span>
      </span>
    </Tooltip>
  );
}

const ROW = 'motion-arrive relative flex h-row min-w-0 items-center gap-3 rounded-control px-row-x transition-colors duration-(--duration-fast) hover:bg-hover';

/** A checkout in its state group: kind, name, branch, the session in it, drift and changes; actions on hover. */
function CheckoutLine({ row, multiMachine, actions }: { row: CheckoutRow; multiMachine: boolean; actions?: ReactNode }) {
  const c = row.checkout;
  const worktree = c.kind === 'worktree';
  const Icon = worktree ? GitBranch : Folder;
  const name = baseName(c.path);
  const branch = c.branch ?? `detached at ${shortSha(c.head)}`;
  return (
    <li className={cn(REVEAL_GROUP, ROW)} data-checkout={c.id}>
      <Tooltip content={worktree ? 'Worktree' : 'Main checkout'}>
        <Icon className="size-4 shrink-0 text-fg-subtle" aria-label={worktree ? 'Worktree' : 'Main checkout'} />
      </Tooltip>
      <Tooltip content={c.error ? `Could not read git: ${c.error}` : `${shortPath(c.path, 80)}\n${shortSha(c.head)} ${c.headSubject}`} delay={500}>
        <span tabIndex={-1} className="max-w-1/3 shrink-0 truncate text-ui font-medium text-fg">
          {name}
        </span>
      </Tooltip>
      {c.error ? (
        <span data-meta="" className="min-w-0 truncate text-caption text-status-danger">
          Could not read git
        </span>
      ) : branch !== name ? (
        <span data-meta="" className="hidden min-w-0 shrink truncate font-mono text-caption text-fg-subtle sm:inline">
          {branch}
        </span>
      ) : null}
      <span data-meta="" className="hidden min-w-0 flex-1 truncate text-caption md:inline">
        {row.session ? (
          <SessionRef id={row.session.id} className="text-fg-muted" />
        ) : multiMachine && row.machine ? (
          <MachineRef id={row.machine.id} className="text-fg-subtle" />
        ) : null}
      </span>
      <RevealRest className="ml-auto flex shrink-0 items-center justify-end gap-4">
        <span className="hidden sm:inline-flex">
          <Drift row={row} />
        </span>
        {c.dirty > 0 && (
          <Tooltip content={`${plural(c.dirty, 'changed file')}, not committed`}>
            <span tabIndex={-1} className="text-caption text-fg-muted num">
              {formatInt(c.dirty)} changed
            </span>
          </Tooltip>
        )}
      </RevealRest>
      {actions && <RevealSlot className="absolute right-row-x flex items-center gap-1">{actions}</RevealSlot>}
    </li>
  );
}

/** A landing in its state group: what it lands, one fact, and how long it has waited or when it finished. */
function LandingLine({ w, now }: { w: Extract<WorkItem, { kind: 'landing' }>; now: number }) {
  const i = w.item;
  const time =
    w.state === 'queued' ? (
      <span role="timer">waiting {formatDuration(now - (i.startedAt ?? now))}</span>
    ) : w.state === 'verifying' ? (
      <span role="timer">{formatDuration(now - (i.stageStartedAt ?? i.startedAt ?? now))}</span>
    ) : (
      <RelativeTime ts={i.endedAt ?? i.startedAt ?? now} />
    );
  return (
    <li className={ROW}>
      <GitMerge className="size-4 shrink-0 text-fg-subtle" aria-hidden />
      <span className="min-w-0 shrink truncate text-ui text-fg">{i.title}</span>
      {i.priority && w.state === 'queued' && (
        <Badge data-chip="" variant="outline" className="shrink-0">
          Priority
        </Badge>
      )}
      <span data-meta="" className="hidden min-w-0 flex-1 truncate text-caption text-fg-subtle lg:inline">
        {i.detail ?? i.by}
      </span>
      <span className="ml-auto shrink-0 text-caption text-fg-subtle num">{time}</span>
    </li>
  );
}

function groupMeta(state: WorkState, items: WorkItem[], now: number): string | undefined {
  const landings = items.filter((i): i is Extract<WorkItem, { kind: 'landing' }> => i.kind === 'landing');
  if (state === 'queued' && landings.length) {
    const oldest = Math.min(...landings.map((l) => l.item.startedAt ?? now));
    return `oldest ${formatDuration(now - oldest)}`;
  }
  if (state === 'landed' && landings.length) return 'last 24 hours';
  if (state === 'stale') return 'no live session';
  return undefined;
}

/**
 * A state group shows this many rows, then folds the rest behind "Show N more". A project with
 * 80 live checkouts drew every row (25,000 elements with 1,200 checkouts) and froze the page as
 * it opened (21 Oct 2026).
 */
const GROUP_ROWS = 6;

/** The rows of one state group, the first GROUP_ROWS of them until asked for the rest. */
function StateItems({ list, label, multiMachine, now, checkoutActions }: { list: WorkItem[]; label: string; multiMachine: boolean; now: number; checkoutActions: (row: CheckoutRow) => ReactNode }) {
  const [all, setAll] = useState(false);
  const shown = all ? list : list.slice(0, GROUP_ROWS);
  return (
    <>
      <ul className="flex min-w-0 flex-col" aria-label={label}>
        {shown.map((w) =>
          w.kind === 'checkout' ? (
            <CheckoutLine key={w.key} row={w.row} multiMachine={multiMachine} actions={checkoutActions(w.row)} />
          ) : (
            <LandingLine key={w.key} w={w} now={now} />
          ),
        )}
      </ul>
      {/* A quiet fold, like the Overview's Needs you: the group opens in place. */}
      {list.length > GROUP_ROWS && (
        <Pressable
          onClick={() => setAll(!all)}
          aria-expanded={all}
          className="mx-3 my-1 flex w-auto items-center gap-1 self-start rounded-control text-caption text-fg-subtle transition-colors duration-(--duration-fast) hover:text-fg-muted"
        >
          {all ? 'Show less' : `Show ${formatInt(list.length - GROUP_ROWS)} more`}
          <ChevronDown className={cn('size-3 transition-transform duration-(--duration-fast)', all && 'rotate-180')} aria-hidden />
        </Pressable>
      )}
    </>
  );
}

/**
 * One project: its name and where it lives, its landing history as a strip, the share of its
 * work in each state as one bar, then the work itself grouped by state. Idle checkouts and
 * finished landings fold away, so a project with 70 checkouts reads as what is happening in it.
 */
export function ProjectCard({
  work,
  now,
  highlighted,
  chip,
  menu,
  checkoutActions,
}: {
  work: ProjectWork;
  now: number;
  highlighted?: boolean;
  chip?: ReturnType<typeof projectChip>;
  menu?: ReactNode;
  checkoutActions: (row: CheckoutRow) => ReactNode;
}) {
  const p = work.project;
  const name = p?.name ?? 'Unknown project';
  const remote = shortRemote(p?.remoteUrl ?? null);
  const multiMachine = work.machines.length > 1;
  const present = WORK_STATES.filter((s) => work.counts[s] > 0);
  const titleId = `project-${work.id}`;
  return (
    <section
      aria-labelledby={titleId}
      data-group={work.id}
      className={cn(CARD_SURFACE, 'motion-arrive flex min-w-0 flex-col transition-colors duration-(--duration-slow)', highlighted && 'border-accent')}
    >
      <header className="flex min-w-0 items-center gap-3 px-4 pt-4">
        <ProjectMark projectId={p?.id ?? null} name={name} size="md" />
        <div className="flex min-w-0 flex-1 flex-col">
          <span className="flex min-w-0 items-center gap-2">
            <h2 id={titleId} className="truncate text-body font-semibold text-fg">
              {/* QA-32: the title opens the project's own work: its missions and backlog. */}
              {p ? (
                <Link to={`/missions?project=${encodeURIComponent(p.id)}`} className="rounded-control underline-offset-2 hover:underline">
                  {name}
                </Link>
              ) : (
                name
              )}
            </h2>
            {chip && (
              <Badge data-chip="" tone={chip.tone} variant={chip.variant} className="hidden shrink-0 sm:inline-flex">
                {chip.text}
              </Badge>
            )}
          </span>
          <span className="flex min-w-0 items-center gap-2 text-caption text-fg-subtle">
            <span data-meta="" className="shrink-0 num">
              {plural(work.rows.length, 'checkout')}
              {multiMachine ? ` on ${plural(work.machines.length, 'machine')}` : work.machines[0] ? ` on ${work.machines[0].name}` : ''}
            </span>
            {remote && (
              <span data-meta="" className="hidden min-w-0 truncate md:inline">
                {remote}
              </span>
            )}
          </span>
        </div>
        {work.runs.length > 0 && (
          <div className="hidden w-48 shrink-0 flex-col items-end gap-1 md:flex">
            <RunStrip runs={work.runs} slots={20} label={`Landings in ${name}: ${runSummary(work.runs)}`} className="w-full" />
            <span className="text-caption text-fg-subtle">{work.lastLanded ? <RelativeTime ts={work.lastLanded} prefix="Landed" /> : 'Nothing landed yet'}</span>
          </div>
        )}
        {menu}
      </header>

      <div className="px-4 pt-4 pb-3">
        <StateSummaryBar counts={work.counts} labels={PROJECT_STATE_LABEL} label={`Work in ${name} by state`} />
      </div>

      <div className="flex min-w-0 flex-col px-1 pb-2">
        {present.map((state) => {
          const list = itemsIn(work.items, state);
          return (
            <StateGroup key={state} state={state} count={list.length} label={PROJECT_STATE_LABEL[state]} meta={groupMeta(state, list, now)}>
              <StateItems list={list} label={`${PROJECT_STATE_LABEL[state] ?? state} in ${name}`} multiMachine={multiMachine} now={now} checkoutActions={checkoutActions} />
            </StateGroup>
          );
        })}
      </div>
    </section>
  );
}

